/* PWA install + iOS hint + Web Push subscribe.
   Renders:
   • A floating "Install app" button when beforeinstallprompt fires
   • An iOS-specific Safari hint banner on iPhone/iPad
   • A "Enable push notifications" button (Web Push API)
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DeviceMobile, BellRinging, X as XIcon, Plus } from "@phosphor-icons/react";

const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
const isInStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

export default function PwaInstall() {
  const [prompt, setPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(localStorage.getItem("span_pwa_dismissed") === "1");
  const [pushState, setPushState] = useState("default"); // 'default' | 'granted' | 'denied' | 'busy'

  useEffect(() => {
    if (dismissed || isInStandalone()) return;
    const handler = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    if (isIOS() && !isInStandalone()) setShowIosHint(true);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [dismissed]);

  useEffect(() => {
    if ("Notification" in window) setPushState(Notification.permission);
  }, []);

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    setPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem("span_pwa_dismissed", "1");
    setDismissed(true); setShowIosHint(false);
  };

  const enablePush = async () => {
    setPushState("busy");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setPushState(perm); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: data.key ? urlBase64ToUint8Array(data.key) : undefined,
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys || {},
        user_agent: navigator.userAgent,
      });
      setPushState("granted");
    } catch (e) {
      console.warn("Push subscription failed:", e);
      setPushState(Notification.permission || "denied");
    }
  };

  if (dismissed || isInStandalone()) return null;

  return (
    <>
      {prompt && (
        <div className="fixed bottom-4 right-4 z-40 card-flat p-3 flex items-center gap-3 max-w-sm shadow-lg" data-testid="pwa-install-banner">
          <DeviceMobile size={20} weight="duotone"/>
          <div className="flex-1 text-[12px]">
            <div className="font-semibold">Install SPAN as an app</div>
            <div className="text-[11px] text-[var(--span-text-2)]">Works offline · push notifications · home-screen access</div>
          </div>
          <button data-testid="pwa-install-btn" onClick={install} className="span-btn h-8 text-[11px]">Install</button>
          <button onClick={dismiss} className="text-[var(--span-text-2)]"><XIcon size={14}/></button>
        </div>
      )}
      {showIosHint && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 card-flat p-3 max-w-sm shadow-lg" data-testid="pwa-ios-hint">
          <div className="flex items-start gap-3">
            <DeviceMobile size={20} weight="duotone"/>
            <div className="flex-1 text-[12px]">
              <div className="font-semibold">Install SPAN on iPhone/iPad</div>
              <div className="text-[11px] text-[var(--span-text-2)] mt-1">
                Tap the Share button, then "Add to Home Screen" — works offline and supports push notifications on iOS 16.4+.
              </div>
            </div>
            <button onClick={dismiss} className="text-[var(--span-text-2)]"><XIcon size={14}/></button>
          </div>
        </div>
      )}
      {!isIOS() && "PushManager" in window && pushState !== "granted" && pushState !== "denied" && (
        <button onClick={enablePush} data-testid="enable-push-btn"
                className="fixed bottom-20 right-4 z-30 span-btn h-8 text-[11px] shadow-lg"
                disabled={pushState === "busy"}>
          <BellRinging size={12}/> {pushState === "busy" ? "Enabling…" : "Enable push notifications"}
        </button>
      )}
    </>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}
